/**
 * The SBA 7(a) upfront guaranty fee.
 *
 * This is the calculation public tools get wrong most often, and the errors are
 * large enough to change what a borrower brings to closing. Three things have to
 * be right, and they are easy to conflate:
 *
 *   1. The TIER is selected by the GROSS approval amount — guaranteed plus
 *      unguaranteed. (Notice 5000-872051, p.1.)
 *   2. The FEE is charged on the GUARANTEED portion only.
 *   3. The top tier is MARGINAL: 3.5% of guaranteed dollars up to and including
 *      $1,000,000, plus 3.75% of guaranteed dollars above that. Applying 3.5% or
 *      3.75% flat to the whole guaranteed portion overstates the fee badly — on a
 *      $5,000,000 loan at 75% guaranty the correct fee is $138,125, where a flat
 *      3.5% reports $175,000.
 *
 * Order of operations matters too: waivers are checked before any tier maths,
 * because a qualifying manufacturer pays nothing at all.
 *
 * Every rate comes from `params.ts`, which carries its citation. Nothing here is
 * a literal rate.
 */

import { round2 } from '../../src/core/rates.js';
import { exposureCaps, guarantyPctFor, guarantyPctWithPriors, scheduleFor } from './params.js';
import type { FeeSchedule, GrossTier, MaturityTier } from './params.js';
import type { FeeBand, FeeInput, FeeResult, LoanType, PriorLoan } from './types.js';

const MONEY = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const PCT = (p: number) => `${round2(p * 100)}%`;

/** WCP and EWCP fees key off maturity, not loan size. */
function isWorkingCapitalPilot(t: LoanType): boolean {
  return t === 'wcp' || t === 'ewcp';
}

function maturityTierPct(tiers: MaturityTier[], maturityMonths: number, label: string): number {
  for (const tier of tiers) {
    if (maturityMonths <= tier.maxMaturityMonths) return tier.pct;
  }
  const top = tiers[tiers.length - 1];
  throw new Error(
    `A ${label} loan with a ${maturityMonths}-month maturity is outside SBA's published fee bands` +
      `${top ? ` (which stop at ${top.maxMaturityMonths} months)` : ''}.`,
  );
}

function tierFor(tiers: GrossTier[], grossAmount: number): GrossTier {
  for (const tier of tiers) {
    if (tier.maxGrossAmount === null || grossAmount <= tier.maxGrossAmount) return tier;
  }
  const { value, citation } = exposureCaps();
  throw new Error(
    `${MONEY(grossAmount)} is above the ${MONEY(value.maxGrossLoanAmount)} maximum 7(a) loan ` +
      `amount, so SBA publishes no fee for it (${citation.source}).`,
  );
}

/** Walk the marginal bands of a tier, keeping each step so it can be shown. */
function marginalFee(
  tiers: GrossTier[],
  grossAmount: number,
  guaranteedAmount: number,
): { fee: number; bands: FeeBand[] } {
  const tier = tierFor(tiers, grossAmount);
  const bands: FeeBand[] = [];
  let consumed = 0;
  let fee = 0;

  for (const band of tier.marginal) {
    const ceiling = band.upToGuaranteed ?? Infinity;
    const inBand = Math.min(guaranteedAmount, ceiling) - consumed;
    if (inBand <= 0) continue;
    const bandFee = inBand * band.pct;
    fee += bandFee;
    consumed += inBand;
    bands.push({
      guaranteedInBand: round2(inBand),
      pct: band.pct,
      fee: round2(bandFee),
      label:
        band.upToGuaranteed === null && bands.length > 0
          ? `${PCT(band.pct)} of the guaranteed amount above ${MONEY(consumed - inBand)}`
          : `${PCT(band.pct)} of ${MONEY(inBand)} guaranteed`,
    });
  }

  return { fee: round2(fee), bands };
}

/** A prior loan's guaranteed portion, the way SBA's own workbook derives it. */
function priorGuaranteed(prior: PriorLoan): number {
  const pct = prior.guarantyPctOverride ?? guarantyPctFor(prior.grossAmount, prior.loanType);
  return prior.grossAmount * pct;
}

/**
 * Loans that aggregate with this one for FEE purposes under the 90-day rule.
 *
 * WCP and EWCP loans are excluded here: the notice combines them for the
 * guaranty percentage and for manufacturer relief, but not for the fee, because
 * their fees are based on maturity.
 */
function feeAggregationSet(priors: PriorLoan[]): PriorLoan[] {
  return priors.filter((p) => !isWorkingCapitalPilot(p.loanType) && p.maturityMonths > 12);
}

function validate(input: FeeInput): void {
  if (!Number.isFinite(input.grossAmount) || input.grossAmount <= 0) {
    throw new Error('grossAmount must be a positive number');
  }
  if (!Number.isInteger(input.maturityMonths) || input.maturityMonths <= 0) {
    throw new Error('maturityMonths must be a positive whole number of months');
  }
}

/**
 * Does the manufacturer waiver apply? Eligibility is tested on the COMBINED
 * amount when other loans fall inside the 90-day window: the notice combines
 * loan amounts "as if they are one loan to determine the percentage of guaranty
 * and Upfront Fee relief for manufacturers".
 */
function manufacturerWaiverApplies(
  input: FeeInput,
  schedule: FeeSchedule,
  combinedGross: number,
): boolean {
  const w = schedule.waivers.manufacturer;
  if (!input.naicsSector) return false;
  if (!w.naicsSectors.includes(input.naicsSector)) return false;
  if (w.excludesMarc && input.isMarc) return false;
  return combinedGross <= w.maxGrossAmount;
}

/**
 * The borrower's upfront guaranty fee, plus the lender's annual service fee
 * reported separately so it can never be added to a borrower total.
 */
export function upfrontFee(input: FeeInput): FeeResult {
  validate(input);

  const { schedule, provisional, notes: scheduleNotes } = scheduleFor(input.approvalDate);
  const notes = [...scheduleNotes];

  const priors = input.priorWithin90Days ?? [];
  // Every 7(a) loan in the window combines for the guaranty percentage and for
  // manufacturer relief, whatever its type.
  const combinedGross =
    input.grossAmount + priors.reduce((sum, p) => sum + p.grossAmount, 0);

  // 13 CFR 120.210: loans inside the window combine, and the later loan absorbs
  // the reduction needed to keep the combined guaranty at or under 75%.
  const guarantyPct = guarantyPctWithPriors({
    grossAmount: input.grossAmount,
    loanType: input.loanType,
    priorGross: priors.reduce((s, p) => s + p.grossAmount, 0),
    priorGuaranteed: priors.reduce((s, p) => s + priorGuaranteed(p), 0),
  });

  const guarantyAmount = round2(input.grossAmount * guarantyPct);

  if (priors.length > 0) {
    notes.push(
      `SBA treats loans approved to you or an affiliate within ${schedule.ninetyDayRule.windowDays} days of each other as one loan when working out the guaranty percentage and the fee, whichever lender made them.`,
    );
  }

  const lenderAnnualFee = {
    pct: schedule.annualServiceFeePct,
    paidBy: 'lender' as const,
    passThroughProhibited: true as const,
    firstYearAmount: round2(guarantyAmount * schedule.annualServiceFeePct),
    explanation: `SBA charges the lender ${PCT(
      schedule.annualServiceFeePct,
    )} a year on the guaranteed balance. Your lender pays this and is not permitted to pass it on to you, so it is not part of your payment.`,
  };

  const citation = {
    source: schedule.source,
    citation: schedule.citation,
    asOf: schedule.asOf,
    verified: schedule.verified,
    ...(schedule._verifyNote ? { verifyNote: schedule._verifyNote } : {}),
  };

  const base = {
    guarantyPct,
    guarantyAmount,
    fiscalYearApplied: schedule.fiscalYear,
    provisional,
    citation,
    lenderAnnualFee,
  };

  // --- waivers first: a qualifying borrower pays nothing at all.
  if (input.loanType === 'express' && input.isVeteranOwned) {
    notes.push(
      `No upfront fee: SBA Express loans to veteran-owned businesses pay $0 by law (${schedule.waivers.veteranExpress.statute}). Your lender must document the eligibility.`,
    );
    return { ...base, upfrontFee: 0, bands: [], notes };
  }

  if (manufacturerWaiverApplies(input, schedule, combinedGross)) {
    const w = schedule.waivers.manufacturer;
    notes.push(
      `No upfront fee: SBA waives it for manufacturers (NAICS ${w.naicsSectors.join(
        '-',
      )}) borrowing ${MONEY(w.maxGrossAmount)} or less.`,
    );
    return { ...base, upfrontFee: 0, bands: [], notes };
  }

  // --- Working Capital Pilot and Export Working Capital: fee keys off maturity.
  if (isWorkingCapitalPilot(input.loanType)) {
    const tiers = input.loanType === 'wcp' ? schedule.wcpTiers : schedule.ewcpTiers;
    const pct = maturityTierPct(
      tiers,
      input.maturityMonths,
      input.loanType === 'wcp' ? 'WCP' : 'EWCP',
    );
    const fee = round2(guarantyAmount * pct);
    notes.push(
      `${input.loanType.toUpperCase()} fees are set by maturity rather than loan size: ${PCT(
        pct,
      )} of the guaranteed amount at ${input.maturityMonths} months.`,
    );
    return {
      ...base,
      upfrontFee: fee,
      bands: [
        {
          guaranteedInBand: guarantyAmount,
          pct,
          fee,
          label: `${PCT(pct)} of ${MONEY(guarantyAmount)} guaranteed`,
        },
      ],
      notes,
    };
  }

  // --- short-term loans: a flat rate on the guaranteed portion.
  if (input.maturityMonths <= schedule.shortTermMaxMaturityMonths) {
    const pct = schedule.shortTermPct;
    const fee = round2(guarantyAmount * pct);
    notes.push(
      `Loans maturing in ${schedule.shortTermMaxMaturityMonths} months or less pay ${PCT(
        pct,
      )} of the guaranteed amount. If the maturity is later extended beyond ${
        schedule.shortTermMaxMaturityMonths
      } months, SBA charges the difference.`,
    );
    return {
      ...base,
      upfrontFee: fee,
      bands: [
        {
          guaranteedInBand: guarantyAmount,
          pct,
          fee,
          label: `${PCT(pct)} of ${MONEY(guarantyAmount)} guaranteed`,
        },
      ],
      notes,
    };
  }

  // --- the ordinary case: tier by gross, marginal on the guaranteed portion.
  const aggregated = schedule.ninetyDayRule.combinesForUpfrontFee
    ? feeAggregationSet(priors)
    : [];

  if (aggregated.length === 0) {
    const { fee, bands } = marginalFee(
      schedule.longTermTiers,
      input.grossAmount,
      guarantyAmount,
    );
    return { ...base, upfrontFee: fee, bands, notes };
  }

  // Under the 90-day rule the fee is what the combined loan would have cost,
  // less what was already paid — and never a negative number.
  const combinedFeeGross = input.grossAmount + aggregated.reduce((s, p) => s + p.grossAmount, 0);
  const combinedGuaranteed = guarantyAmount + aggregated.reduce((s, p) => s + priorGuaranteed(p), 0);
  const alreadyPaid = aggregated.reduce((s, p) => s + p.feePaid, 0);

  const { fee: combinedFee, bands } = marginalFee(
    schedule.longTermTiers,
    combinedFeeGross,
    combinedGuaranteed,
  );
  const due = schedule.ninetyDayRule.feeNeverNegative
    ? Math.max(0, round2(combinedFee - alreadyPaid))
    : round2(combinedFee - alreadyPaid);

  notes.push(
    `Combined with your other recent loan${
      aggregated.length > 1 ? 's' : ''
    }, the fee on ${MONEY(combinedFeeGross)} would be ${MONEY(
      combinedFee,
    )}. Less the ${MONEY(alreadyPaid)} already paid, this loan's fee is ${MONEY(due)}.`,
  );

  return { ...base, upfrontFee: due, bands, notes };
}

/**
 * SBA's two exposure ceilings, checked together: the gross amount of loans
 * approved within the 90-day window, and total SBA guaranty across every
 * approved loan — which counts 504 guarantees toward the 7(a) ceiling.
 *
 * Reported rather than thrown, so a caller can refuse the deal and explain why
 * instead of pricing something that cannot be done.
 */
export function checkExposure(args: {
  grossAmount: number;
  priorWithin90Days?: PriorLoan[];
  /** Guaranteed amounts on 7(a) loans approved more than 90 days ago. */
  otherGuaranteed7a?: number;
  /** Guaranteed amounts on existing 504 loans. */
  existing504Guaranteed?: number;
  loanType?: LoanType;
}): { ok: boolean; violations: string[] } {
  const { value, citation } = exposureCaps();
  const priors = args.priorWithin90Days ?? [];
  const violations: string[] = [];

  const grossInWindow = args.grossAmount + priors.reduce((s, p) => s + p.grossAmount, 0);
  if (grossInWindow > value.maxGrossLoanAmount) {
    violations.push(
      `Loans approved within 90 days of each other cannot total more than ${MONEY(
        value.maxGrossLoanAmount,
      )}; these come to ${MONEY(grossInWindow)}.`,
    );
  }

  const guaranteed =
    args.grossAmount * guarantyPctFor(grossInWindow, args.loanType ?? '7a') +
    priors.reduce((s, p) => s + priorGuaranteed(p), 0) +
    (args.otherGuaranteed7a ?? 0) +
    (args.existing504Guaranteed ?? 0);

  if (guaranteed > value.maxTotalSbaGuarantyIncluding504) {
    violations.push(
      `SBA's total guaranty across all your approved loans, including any 504 loans, cannot exceed ${MONEY(
        value.maxTotalSbaGuarantyIncluding504,
      )}; these come to ${MONEY(guaranteed)}.`,
    );
  }

  if (violations.length > 0 && !citation.verified) {
    violations.push(
      `These limits are drawn from SBA's own fee calculator and have not yet been confirmed in the regulation itself — check them with your lender.`,
    );
  }

  return { ok: violations.length === 0, violations };
}
